import { useEffect, useMemo, useRef, useState } from 'react'
import { euro, families, products } from './data.js'

const Icon = ({ name, size = 22 }) => {
  const paths = {
    bag: <><path d="M6 8h12l1 12H5L6 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></>,
    close: <><path d="m6 6 12 12M18 6 6 18"/></>,
    minus: <path d="M5 12h14"/>,
    plus: <><path d="M12 5v14M5 12h14"/></>,
    arrow: <><path d="M5 12h14M14 7l5 5-5 5"/></>,
    back: <><path d="M19 12H5m5-5-5 5 5 5"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    leaf: <><path d="M5 19C6 9 12 4 20 4c0 8-5 14-15 15Z"/><path d="M6 18c4-4 7-7 12-11"/></>,
    chevron: <path d="m9 18 6-6-6-6"/>,
  }
  return <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>
}

const useHashRoute = () => {
  const [hash, setHash] = useState(window.location.hash || '#/')
  useEffect(() => {
    const onHash = () => setHash(window.location.hash || '#/')
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  return hash.replace(/^#\/?/, '').split('/').filter(Boolean)
}

function BrandLogo() {
  return (
    <span className="brand-lockup">
      <img className="brand-logo" src="/brand/origen-logo.png" alt="ORIGEN" width="960" height="199" />
      <small>Turrones artesanos</small>
    </span>
  )
}

function Header({ count, onOpenCart }) {
  return (
    <header className="site-header">
      <a className="brand" href="#/" aria-label="Origen Artesano, inicio">
        <BrandLogo />
      </a>
      <nav aria-label="Navegación principal">
        <a href="#catalogo">Turrones</a>
        <a href="#proceso">Elaboración</a>
      </nav>
      <button className="bag-button" onClick={onOpenCart} aria-label={`Abrir cesta, ${count} productos`}>
        <Icon name="bag" size={20} />
        {count > 0 && <span className="bag-count">{count}</span>}
      </button>
    </header>
  )
}

function Hero() {
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero-copy reveal">
        <p className="eyebrow">Turrones artesanos de Jijona</p>
        <h1 id="hero-title">La tradición,<br/><span>afinada.</span></h1>
        <p className="hero-lead">Almendra Marcona. Miel de azahar. Tiempo.<br/> Solo lo esencial, llevado a su mejor expresión.</p>
        <div className="hero-actions">
          <a className="button primary" href="#catalogo">Descubrir la colección</a>
          <a className="text-link" href="#proceso">Cómo lo hacemos <Icon name="chevron" size={17} /></a>
        </div>
      </div>
      <div className="hero-visual reveal delay-1">
        <div className="almond-orbit" aria-hidden="true"><span/><span/><span/></div>
        <img src="/products-compact/piedra-300.webp" alt="Turrón a la piedra ORIGEN sobre una superficie clara" />
        <div className="hero-spec">
          <span>Selección</span>
          <strong>60% Marcona</strong>
          <small>Limón · Canela · Piedra</small>
        </div>
      </div>
    </section>
  )
}

function ProductCard({ product, onAdd }) {
  return (
    <article className="product-card">
      <a className="product-image" href={`#/producto/${product.slug}`} aria-label={`${product.badge ? `${product.badge}. ` : ''}Ver detalle de ${product.name}, ${product.format}`}>
        {product.badge && <span className="badge" aria-hidden="true">{product.badge}</span>}
        <img loading="lazy" src={product.image} alt={`${product.name} ${product.format}`} />
        <span className="view-label">Ver detalle <Icon name="arrow" size={17}/></span>
      </a>
      <div className="product-info">
        <div>
          <p>{product.family}</p>
          <h3><a href={`#/producto/${product.slug}`}>{product.name}</a></h3>
          <span>{product.format}</span>
        </div>
        <div className="product-buy">
          <strong>{euro(product.price)}</strong>
          <button onClick={() => onAdd(product)} aria-label={`Añadir ${product.name} ${product.format} a la cesta`}><Icon name="plus" size={19}/></button>
        </div>
      </div>
    </article>
  )
}

function Catalog({ onAdd }) {
  const [family, setFamily] = useState('Todos')
  const filtered = family === 'Todos' ? products : products.filter((p) => p.family === family)
  return (
    <section className="catalog" id="catalogo" aria-labelledby="catalog-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">La colección completa</p>
          <h2 id="catalog-title">Dieciocho formas de entender el turrón.</h2>
        </div>
        <p>Recetas honestas, elaboradas en pequeños lotes y presentadas con todos sus detalles.</p>
      </div>
      <div className="filters" role="group" aria-label="Filtrar por familia">
        {families.map((item) => <button key={item} className={family === item ? 'active' : ''} onClick={() => setFamily(item)} aria-pressed={family === item}>{item}</button>)}
      </div>
      <div className="product-grid">
        {filtered.map((product) => <ProductCard key={product.id} product={product} onAdd={onAdd}/>) }
      </div>
    </section>
  )
}

function Process() {
  return (
    <section className="process" id="proceso" aria-labelledby="process-title">
      <div className="process-image">
        <img loading="lazy" src="/products-compact/jijona-300.webp" alt="Detalle de turrón de Jijona ORIGEN elaborado con almendra Marcona" />
      </div>
      <div className="process-copy">
        <p className="eyebrow">El tiempo es un ingrediente</p>
        <h2 id="process-title">Hasta cuatro horas de Boixet. Ningún atajo.</h2>
        <p>La almendra se tuesta, la miel se cuece y la mezcla se refina antes de entrar en el Boixet. A fuego lento alcanza la textura que define al auténtico turrón de Jijona.</p>
        <dl>
          <div><dt>Origen</dt><dd>Jijona, Alicante</dd></div>
          <div><dt>Almendra</dt><dd>Marcona calibre 14–16</dd></div>
          <div><dt>Proceso</dt><dd>Artesano · Lotes pequeños</dd></div>
        </dl>
      </div>
    </section>
  )
}

function Home({ onAdd }) {
  return <main><Hero/><Catalog onAdd={onAdd}/><Process/></main>
}

function ProductDetail({ product, onAdd }) {
  if (!product) return <NotFound />
  return (
    <main className="detail-page">
      <a className="back-link" href="#/"><Icon name="back" size={18}/> Volver a la colección</a>
      <section className="detail-hero">
        <div className="detail-image"><img src={product.image} alt={`${product.name} ${product.format}`}/></div>
        <div className="detail-summary">
          <p className="eyebrow">{product.family} · {product.format}</p>
          <h1>{product.name}</h1>
          <p className="detail-intro">{product.short}</p>
          <div className="detail-price"><strong>{euro(product.price)}</strong><span>IVA incluido</span></div>
          <button className="button primary wide" onClick={() => onAdd(product)}>Añadir a la cesta</button>
          <p className="shipping-note"><Icon name="check" size={17}/> Envío gratuito a partir de 80 €</p>
        </div>
      </section>
      <section className="specs" aria-label="Características del producto">
        <div><span>Ingredientes</span><h2>La receta.</h2><p>{product.ingredients}</p></div>
        <div><span>Elaboración</span><h2>El oficio.</h2><p>{product.process}</p></div>
        <div><span>Alérgenos</span><h2>Todo claro.</h2><p>{product.allergens}</p></div>
      </section>
      <section className="related">
        <div className="section-heading"><div><p className="eyebrow">También te gustarán</p><h2>De la misma familia.</h2></div></div>
        <div className="product-grid compact">
          {products.filter((p) => p.family === product.family && p.id !== product.id).slice(0,3).map((p) => <ProductCard key={p.id} product={p} onAdd={onAdd}/>)}
        </div>
      </section>
    </main>
  )
}

function CartDrawer({ open, cart, onClose, onChange, onCheckout }) {
  const panelRef = useRef(null)
  const count = cart.reduce((n, item) => n + item.qty, 0)
  const total = cart.reduce((n, item) => n + item.price * item.qty, 0)
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement
    document.body.classList.add('no-scroll')
    panelRef.current?.focus()
    const onKey = (event) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => { document.body.classList.remove('no-scroll'); window.removeEventListener('keydown', onKey); previous?.focus?.() }
  }, [open, onClose])
  return (
    <div className={`drawer-shell ${open ? 'open' : ''}`} aria-hidden={!open}>
      <button className="drawer-backdrop" onClick={onClose} tabIndex={open ? 0 : -1} aria-label="Cerrar cesta"/>
      <aside className="cart-drawer" ref={panelRef} tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="cart-title">
        <div className="drawer-header"><div><p className="eyebrow">Tu selección</p><h2 id="cart-title">Cesta <span>{count}</span></h2></div><button className="icon-button" onClick={onClose} aria-label="Cerrar"><Icon name="close"/></button></div>
        {cart.length === 0 ? (
          <div className="empty-cart"><span><Icon name="bag" size={30}/></span><h3>Tu cesta está esperando.</h3><p>Descubre los turrones y añade tus favoritos.</p><button className="button secondary" onClick={onClose}>Ver la colección</button></div>
        ) : (
          <>
            <div className="cart-lines">{cart.map((item) => (
              <div className="cart-line" key={item.id}>
                <img src={item.image} alt=""/>
                <div className="cart-line-copy"><h3>{item.name}</h3><p>{item.format}</p><div className="quantity"><button onClick={() => onChange(item.id, -1)} aria-label={`Quitar una unidad de ${item.name}`}><Icon name="minus" size={15}/></button><span>{item.qty}</span><button onClick={() => onChange(item.id, 1)} aria-label={`Añadir una unidad de ${item.name}`}><Icon name="plus" size={15}/></button></div></div>
                <strong>{euro(item.price * item.qty)}</strong>
              </div>
            ))}</div>
            <div className="drawer-footer">
              <div className="delivery-progress"><div><span>Envío</span><strong>{total >= 80 ? 'Gratis' : `${euro(80-total)} para envío gratis`}</strong></div><progress max="80" value={Math.min(total,80)} aria-label="Progreso hacia el envío gratuito"/></div>
              <div className="total"><span>Total</span><strong>{euro(total)}</strong></div>
              <button className="button primary wide" onClick={onCheckout}>Continuar al pago</button>
              <small>Pago seguro simulado · No se realizará ningún cargo</small>
            </div>
          </>
        )}
      </aside>
    </div>
  )
}

function Checkout({ cart, onComplete }) {
  const [submitting, setSubmitting] = useState(false)
  const total = cart.reduce((n, item) => n + item.price * item.qty, 0)
  const shipping = total >= 80 ? 0 : 4.95
  if (!cart.length) return <NotFound title="Tu cesta está vacía" copy="Añade algún turrón antes de continuar." />
  const submit = (event) => {
    event.preventDefault(); setSubmitting(true)
    setTimeout(onComplete, 850)
  }
  return (
    <main className="checkout-page">
      <a className="back-link" href="#/"><Icon name="back" size={18}/> Seguir comprando</a>
      <div className="checkout-layout">
        <form className="checkout-form" onSubmit={submit}>
          <p className="eyebrow">Finalizar compra</p><h1>Todo listo en unos minutos.</h1>
          <fieldset><legend>Contacto</legend><label>Email<input required type="email" autoComplete="email" placeholder="tu@email.com"/></label></fieldset>
          <fieldset><legend>Entrega</legend><div className="form-grid"><label className="full">Nombre completo<input required autoComplete="name"/></label><label className="full">Dirección<input required autoComplete="street-address"/></label><label>Código postal<input required inputMode="numeric" autoComplete="postal-code" pattern="[0-9]{5}"/></label><label>Ciudad<input required autoComplete="address-level2"/></label><label className="full">Provincia<select required defaultValue=""><option value="" disabled>Selecciona una provincia</option><option>Alicante</option><option>Barcelona</option><option>Madrid</option><option>Valencia</option><option>Otra</option></select></label></div></fieldset>
          <fieldset><legend>Pago</legend><div className="payment-mock"><span><Icon name="check" size={17}/></span><div><strong>Pago de demostración</strong><p>No se solicitarán datos bancarios ni se realizará ningún cargo.</p></div></div></fieldset>
          <button className="button primary wide" disabled={submitting}>{submitting ? 'Confirmando…' : `Confirmar pedido · ${euro(total + shipping)}`}</button>
        </form>
        <aside className="order-summary"><h2>Tu pedido</h2>{cart.map((item) => <div className="summary-line" key={item.id}><img src={item.image} alt=""/><div><strong>{item.name}</strong><span>{item.format} · Cantidad {item.qty}</span></div><b>{euro(item.price*item.qty)}</b></div>)}<dl><div><dt>Subtotal</dt><dd>{euro(total)}</dd></div><div><dt>Envío</dt><dd>{shipping ? euro(shipping) : 'Gratis'}</dd></div><div className="grand-total"><dt>Total</dt><dd>{euro(total+shipping)}</dd></div></dl></aside>
      </div>
    </main>
  )
}

function Confirmation() {
  return <main className="confirmation"><div className="success-mark"><Icon name="check" size={38}/></div><p className="eyebrow">Pedido confirmado</p><h1>Gracias. Ya está en marcha.</h1><p>Hemos preparado una confirmación de demostración. En una tienda conectada recibirías ahora todos los detalles por email.</p><div className="confirmation-card"><span>Pedido</span><strong>OA-{String(Date.now()).slice(-6)}</strong><span>Estado</span><strong>Confirmado</strong></div><a className="button primary" href="#/">Volver al inicio</a></main>
}

function NotFound({ title='No encontramos este turrón', copy='Puede que el enlace haya cambiado.' }) {
  return <main className="confirmation"><h1>{title}</h1><p>{copy}</p><a className="button primary" href="#/">Volver a la colección</a></main>
}

function Footer() {
  return <footer><a className="brand" href="#/" aria-label="ORIGEN, inicio"><BrandLogo /></a><p>Avda. de la Pau, 53 · Platja de Gandia</p><span>© 2026 ORIGEN</span></footer>
}

export default function App() {
  const route = useHashRoute()
  const [cart, setCart] = useState(() => { try { const saved = JSON.parse(localStorage.getItem('oa-cart')); return Array.isArray(saved) ? saved.flatMap(item => { const product = products.find(p => p.id === item.id); return product && Number.isInteger(item.qty) && item.qty > 0 ? [{ ...product, qty: item.qty }] : [] }) : [] } catch { return [] } })
  const [cartOpen, setCartOpen] = useState(false)
  const count = cart.reduce((n, item) => n + item.qty, 0)
  useEffect(() => localStorage.setItem('oa-cart', JSON.stringify(cart)), [cart])
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); setCartOpen(false) }, [route.join('/')])
  const add = (product) => {
    setCart((current) => current.some((item) => item.id === product.id) ? current.map((item) => item.id === product.id ? {...item, qty: item.qty + 1} : item) : [...current, {...product, qty: 1}])
    setCartOpen(true)
  }
  const change = (id, delta) => setCart((current) => current.map((item) => item.id === id ? {...item, qty: item.qty + delta} : item).filter((item) => item.qty > 0))
  const complete = () => { setCart([]); window.location.hash = '#/confirmacion' }
  const page = useMemo(() => {
    if (route[0] === 'producto') return <ProductDetail product={products.find((p) => p.slug === route[1])} onAdd={add}/>
    if (route[0] === 'checkout') return <Checkout cart={cart} onComplete={complete}/>
    if (route[0] === 'confirmacion') return <Confirmation/>
    return <Home onAdd={add}/>
  }, [route.join('/'), cart])
  return <><Header count={count} onOpenCart={() => setCartOpen(true)}/>{page}<Footer/><CartDrawer open={cartOpen} cart={cart} onClose={() => setCartOpen(false)} onChange={change} onCheckout={() => { setCartOpen(false); window.location.hash='#/checkout' }}/></>
}
